import { Component } from '@angular/core';
import { FormBuilder, Validators, FormArray, FormGroup, ValidationErrors, AbstractControl } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil, filter } from 'rxjs/operators';
import { of, pipe } from 'rxjs';
import { carFields } from '../create-lead-car/create-lead-car.component';
import { MatDialogRef } from '@angular/material/dialog';
import { TRAILER_TYPE } from '../../../../constants';
import { ADDRESS_AUTOCOMPLETE_MIN_LENGTH, AddressAutocompleteService } from '../../../../providers/address-autocomplete/address-autocomplete.service';
import { HereGeocodeResult, HereService } from '../../../../providers/here/here.service';
import { CreateLeadRequest } from '../../../interfaces/createLeadRequest';
import { trackExecution } from '../../../rxjs/track-execution.operator';
import { DestroyableComponent } from '../../../utils/destroyable.component';
import { LoaderType, Loader } from '../../../utils/loader.util';
import { LocationDTO } from '../../../../interfaces/locationDTO';

@Component({
  selector: 'app-create-lead',
  templateUrl: './create-lead.component.html',
  styleUrls: ['./create-lead.component.scss'],
})
export class CreateLeadComponent extends DestroyableComponent {
  private readonly debounceTime = 500;
  trailerTypes = TRAILER_TYPE;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly hereService: HereService,
    private readonly addressAutocompleteService: AddressAutocompleteService,
    public dialogRef: MatDialogRef<CreateLeadComponent>,
  ) {
    super();
    this.createLeadForm = this.formBuilder.group({
      ...this.getFields(),
      cars: this.formBuilder.array([], Validators.required),
    });
    this.cars = this.createLeadForm.get(this.fieldsArray.cars.name) as FormArray;
    this.carFormGroups = this.cars.controls as FormGroup[];
    this.pickupLocationOptions$ = this.createLeadForm
      .get(this.fields.pickupLocation.name).valueChanges
      .pipe(this.searchLocation);
    this.deliveryLocationOptions$ = this.createLeadForm
      .get(this.fields.deliveryLocation.name).valueChanges
      .pipe(this.searchLocation);
  }

  private readonly locationSelectionValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value && typeof value === 'object') {
      const selection = value as HereGeocodeResult;
      return selection.city && selection.state && selection.zipCode ? null : { invalidLocation: true };
    }

    return value ? { invalidLocation: true } : null;
  };

  public readonly fields = {
    pickupLocation: {
      name: 'pickupLocation',
      validator: ['', [Validators.required, this.locationSelectionValidator]],
    },
    deliveryLocation: {
      name: 'deliveryLocation',
      validator: ['', [Validators.required, this.locationSelectionValidator]],
    },
    email: {
      name: 'email',
      validator: ['', [Validators.required, Validators.email]],
    },
    trailerType: {
      name: 'trailerType',
      validator: ['', [Validators.required]],
    },
  };

  private readonly fieldsArray = {
    cars: {
      name: 'cars',
    },
  };

  loaderKey: LoaderType = 'local';
  loader = Loader.create().add(this.loaderKey);
  loading$ = this.loader.isLoadingObservable(this.loaderKey);

  public createLeadForm: FormGroup;
  cars: FormArray;
  carFormGroups: FormGroup[];
  pickupLocationOptions$;
  deliveryLocationOptions$;

  safeDebounce = pipe(
    takeUntil(this.onDestroy),
    debounceTime(this.debounceTime),
    distinctUntilChanged(),
  );

  searchLocation = pipe(
    this.safeDebounce,
    map((value: unknown) => typeof value === 'string'
      ? this.addressAutocompleteService.normalizeQuery(value)
      : ''),
    filter((searchText: string) => this.addressAutocompleteService.hasMinimumQueryLength(
      searchText,
      ADDRESS_AUTOCOMPLETE_MIN_LENGTH,
    )),
    trackExecution(this.loaderKey, (searchText: string) => this.hereService.getGeocode(searchText).pipe(
      catchError(() => of([])),
      map(elements => elements
        .map(x => ({
          ...x,
          label: [x.address, x.city, x.state, x.zipCode].filter(Boolean).join(', '),
        }))),
      // tslint:disable-next-line: align
    ), this.loader),
  );

  public removeCar(index: number) {
    this.cars.removeAt(index);
  }

  public addCar() {
    this.cars.push(this.formBuilder.group({
      ...this.getCarFields(),
    }));
  }

  public onSubmit() {
    const payload: CreateLeadRequest = {
      ...this.createLeadForm.value,
      pickupLocation: this.toLocation(this.createLeadForm.value.pickupLocation),
      deliveryLocation: this.toLocation(this.createLeadForm.value.deliveryLocation),
    };

    this.dialogRef.close(payload);
  }

  public displayLocation(value: HereGeocodeResult | string | null): string {
    return this.addressAutocompleteService.getDisplayValue(value);
  }

  private getCarFields() {
    return Object.entries(carFields)
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value.validator }), {});
  }

  private getFields() {
    return Object.entries(this.fields)
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value.validator }), {});
  }

  private toLocation(value: HereGeocodeResult | string | null | undefined): LocationDTO {
    const selection = value && typeof value === 'object' ? value as HereGeocodeResult : null;
    const patch = this.addressAutocompleteService.buildSelectionPatch(selection);

    return {
      address: patch.address,
      city: patch.city,
      state: patch.state,
      zipCode: patch.zipCode,
      lat: selection && selection.lat ? Number(selection.lat) : undefined,
      lon: selection && selection.lon ? Number(selection.lon) : undefined,
    };
  }
}

interface IField {
  name: string;
  validator: [string, ((control: AbstractControl) => ValidationErrors)[]];
}

export interface ICarFileds {
  year: IField;
  make: IField;
  model: IField;
  type: IField;
}
